<template>
  <div class="form">
    <div class="form__content">
      <IdentityQueueStepCounter />
      <h1 class="extra-large-headline">
        {{ $t("PageTitle.headline.sourceOfFundsPageHeader") }}
      </h1>
      <p class="bodycopy">
        {{ $t("PageSubTitle.paragraph.purposeOfTransactionInstructions") }}
      </p>
    </div>
    <form @submit.prevent>
      <AutocompleteSelect
        v-if="showCountrySelect"
        v-model:autocomplete="form.country"
        :type="'text'"
        :items="countryList"
        :label="$t('Autocomplete.label.countryResidence')"
        :placeholder="'Search a country'"
        :errors="sourceOfFundsStore.getErrorByField('country')"
        hasIcon
      />

      <FormSelect
        name="employmentStatus"
        type="text"
        :items="sofFormAnswers.employmentStatus"
        :label="$t('FormSelect.label.employmentStatus')"
        @dropdownValueUpdate="updateDropdown"
      />
      <FormSelect
        type="text"
        name="industryType"
        :label="$t('FormSelect.label.industryType')"
        :items="sofFormAnswers.industryType"
        @dropdownValueUpdate="updateDropdown"
      />
      <FormSelect
        type="text"
        name="beneficiaries"
        :label="$t('FormSelect.label.beneficiaries')"
        :items="sofFormAnswers.beneficiaries"
        @dropdownValueUpdate="updateDropdown"
      />
      <FormSelect
        name="yearlyAmount"
        type="text"
        :label="$t('FormSelect.label.yearlyAmount')"
        :items="sofFormAnswers.yearlyAmount"
        @dropdownValueUpdate="updateDropdown"
      />

      <FormSelect
        name="potMainPurpose"
        type="text"
        :items="sofFormAnswers.potMainPurpose"
        :label="$t('FormSelect.label.potMainPurpose')"
        @dropdownValueUpdate="updateDropdown"
      />
      <FormSelect
        :maxItems="3"
        :isMultiSelect="true"
        name="originOfFunds"
        type="text"
        :items="sofFormAnswers.originOfFunds"
        :label="$t('FormSelect.label.originOfFunds')"
        @dropdownValueUpdate="updateDropdown"
      />

      <Checkbox
        name="tnc"
        v-model:tnc="form.tnc"
        v-model:value="form.tnc"
        :text="$t('Checkbox.text.pepSofTnc')"
        :errors="sourceOfFundsStore.getErrorByField('tnc')"
      />

      <ButtonBase
        :isDisabled="
          sourceOfFundsStore.formHasErrors || globalStore.getIsFormLoading
        "
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        {{ $t("ButtonBase.label.continue") }}
      </ButtonBase>
      <IdentityQueueNext />
    </form>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { useSourceOfFundsFormStore } from "@/stores/forms/sourceOfFundsFormStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions.js";

import FormSelect from "@/components/ui/selects/FormSelect.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";
import IdentityQueueStepCounter from "@/components/layout/progress/IdentityQueueStepCounter.vue";
import IdentityQueueNext from "@/components/layout/progress/IdentityQueueNext.vue";

const route = useRoute();
const router = useRouter();

const globalStore = useGlobalStore();
const sourceOfFundsStore = useSourceOfFundsFormStore();
const verificationStore = useVerificationStore();
const identityQueueStore = useIdentityQueueStore();
const { fetchCountries } = useGlobalCountriesActions();
const { postRequest } = useApiHelpers();
const { fetchCustomerId } = useCustomerActions();
const { watchAndUpdateStore } = useWatchAndUpdate();
const countryList = ref([]);
// todo mocked for now, to get from an endpoint
const sofFormAnswers = ref({
  employmentStatus: [
    "Self-employed",
    "Employed",
    "Retired",
    "Unemployed",
    "Full time students",
  ],
  industryType: [
    "Agriculture, Food and Natural Resources",
    "Construction",
    "Architecture",
    "Arts, Audio/Video Technology and Communications",
    "Business Management and Adminstration",
    "Education and Training",
    "Finance",
    "Government and Public Adminstration",
    "Health and Science (eg. medical personnel",
    "Hospitality and Tourism",
    "Human Services (eg. social workers)",
    "Information Technology",
    "Law, Public Safety, Connections and Security",
    "Manufacturing",
    "Marketing, Sales and Service",
    "Non-Governmental Organistion/Charity",
    "Professional Service Provider/Consulting",
    "Real Estate",
    "Science, Technology, Engineering and Mathematics",
  ],
  beneficiaries: ["Yes", "No"],
  yearlyAmount: [
    "Up to 15,000",
    "15,000 to 30,000",
    "30,000 to 50,000",
    "50,000 to 70,000",
    "70,000 to 100,000",
    "100,000 and more",
  ],
  potMainPurpose: [
    "Investment",
    "Trading",
    "Payment for online purchases",
    "Payment for services",
    "Gaming",
    "Other",
    "Ransomware / virus/ cyber extortion demands",
  ],
  originOfFunds: [
    "Personal Savings",
    "Salary",
    "Business Income/Profits",
    "Pension",
    "Government Benefits",
    "Income from Cryptocurrency Trading",
    "Income from Investments (eg. shares, bonds, investment funds)",
    "Income from sale of real estate or other property",
    "Income from renting/leasing",
    "Private loan from family members / friends / others",
    "Loan from bank",
    "Inheritance",
    "Gift",
    "Other",
  ],
});
const potMainPurposeAnswers = [
  "Investment",
  "Trading",
  "Payment for online purchases",
  "Payment for services",
  "Gaming",
  "Other",
  "Ransomware / virus/ cyber extortion demands",
  "Other",
];

const showCountrySelect = ref(false);
const form = ref({
  country:
    (verificationStore.form.country.value &&
      globalStore.getCountryCode(verificationStore.form.country.value)) ||
    "",
  employmentStatus: sourceOfFundsStore.form.employmentStatus.value,
  industryType: sourceOfFundsStore.form.industryType.value,
  beneficiaries: sourceOfFundsStore.form.beneficiaries.value,
  yearlyAmount: sourceOfFundsStore.form.yearlyAmount.value,
  potMainPurpose: sourceOfFundsStore.form.potMainPurpose.value,
  originOfFunds: sourceOfFundsStore.form.originOfFunds.value,
  tnc: sourceOfFundsStore.form.tnc.value || false,
});

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  form.value[field] = value;
};

const handleFormSubmit = async () => {
  globalStore.setIsFormLoading(true);
  const custId = await fetchCustomerId();

  const formData = {
    documentType: "source_of_funds",
    documentReference: custId,
    declaredSourceOfFunds: {
      country_of_residence: form.value.country,
      employment_status: form.value.employmentStatus,
      employment_industry: form.value.industryType,
      expected_amount: form.value.yearlyAmount,
      purpose_of_transaction: [form.value.potMainPurpose],
      origin_of_funds: form.value.originOfFunds,
    },
  };

  postRequest("documents/edd", formData, () => {
    identityQueueStore.updateToNextRoute();
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });
  });
};

watchAndUpdateStore(form, sourceOfFundsStore, () => {});

onMounted(async () => {
  const formattedCountries = await fetchCountries();
  countryList.value = formattedCountries;
  showCountrySelect.value = !form.value.country;
});
</script>
